/**
 * MyAccount.js
 * User account management page with plan banner and two-column layout
 *
 * LAYOUT:
 * Banner: Plan & Usage (full width)
 * Column 1: Update Profile, Change Password
 * Column 2: Welcome to CKG, Export Data, Delete Account (coming soon)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authService, exportService, aiService, knowledgeService } from '../../services/api';
import { Download, FileText, Trash2, DatabaseBackup, Eye, EyeOff, X } from 'lucide-react';
import './MyAccount.css';
import WelcomeModal from './WelcomeModal';
import DeleteConfirmPopover from '../shared/DeleteConfirmPopover';

// =========================================
// Overview Sections
// =========================================
// The Overview modal is reference material, opened one section at a time. Each
// entry below is a separate button on the welcome card; `section` is passed to
// WelcomeModal, which filters its panels to that value.
const OVERVIEW_SECTIONS = [
  { section: 'getting-started', label: 'Getting Started' },
  { section: 'knowledge-entry', label: 'Making a Knowledge Entry' },
  { section: 'explorer', label: 'AI Explorer & Constellations' },
  { section: 'beyond-basics', label: 'Beyond the Basics' }
];

// =========================================
// Main Component
// =========================================

const MyAccount = () => {
  const { user, updateUser, hasSubscriberAccess } = useAuth();
  
  // =========================================
  // State Management
  // =========================================
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    firstName: '',
    lastName: '',
    email: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: ''
  });

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // UI states
  const [profileError, setProfileError] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  // Overview modal: the active section id, or null when closed. There is no
  // auto-open path — it opens only from the buttons on the welcome card.
  const [activeSection, setActiveSection] = useState(null);

  // Export states
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [exportSuccess, setExportSuccess] = useState(null);

  // Remove-starters states
  const [isRemovingStarters, setIsRemovingStarters] = useState(false);
  const [removeStartersError, setRemoveStartersError] = useState(null);
  const [removeStartersSuccess, setRemoveStartersSuccess] = useState(null);
  // Anchor element for the remove-starters confirmation popover, or null.
  const [removeStartersAnchor, setRemoveStartersAnchor] = useState(null);

  // Password change banner state
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Plan & Usage state
  const [queryUsage, setQueryUsage] = useState(null);

  // =========================================
  // Effects
  // =========================================

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setProfileData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || ''
      });
    }
  }, [user]);

  // Fetch AI Explorer usage for freemium users
  useEffect(() => {
    if (user && !hasSubscriberAccess) {
      aiService.getUsage().then(usage => {
        if (usage) setQueryUsage(usage);
      });
    }
  }, [user, hasSubscriberAccess]);

  // =========================================
  // Event Handlers
  // =========================================

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileMessage('');
    setIsProfileLoading(true);

    try {
      const response = await authService.updateProfile(profileData);
      await updateUser(response.user);
      setProfileMessage('Profile updated successfully');
    } catch (err) {
      setProfileError(
        err.response?.data?.message || err.message || 'Profile update failed'
      );
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordMessage('');
    setIsPasswordLoading(true);

    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      setPasswordError("New passwords don't match");
      setIsPasswordLoading(false);
      return;
    }

    try {
      // When mustChangePassword is true (welcome flow), skip current password
      const payload = user?.mustChangePassword
        ? { newPassword: passwordData.newPassword }
        : { currentPassword: passwordData.currentPassword, newPassword: passwordData.newPassword };

      const response = await authService.changePassword(payload);

      setPasswordMessage(response.message || 'Password changed successfully. Please log in again.');
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmNewPassword: ''
      });

      // Handle the requiresLogin flag from the backend
      if (response.requiresLogin) {
        // Redirect to login after a short delay so user can see the success message
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      }
    } catch (err) {
      setPasswordError(
        err.response?.data?.message || err.message || 'Password change failed'
      );
    } finally {
      setIsPasswordLoading(false);
    }
  };

  // =========================================
  // Export Handlers
  // =========================================

  /**
   * Handle JSON export button click
   */
  const handleExportJSON = async () => {
    setIsExporting(true);
    setExportError(null);
    setExportSuccess(null);

    try {
      await exportService.exportAsJSON();
      setExportSuccess('Your knowledge base has been exported as JSON!');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setExportSuccess(null);
      }, 5000);
    } catch (error) {
      console.error('Export JSON error:', error);
      setExportError('Failed to export as JSON. Please try again.');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setExportError(null);
      }, 5000);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Handle CSV export button click
   */
  const handleExportCSV = async () => {
    setIsExporting(true);
    setExportError(null);
    setExportSuccess(null);

    try {
      await exportService.exportAsCSV();
      setExportSuccess('Your knowledge base has been exported as CSV!');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setExportSuccess(null);
      }, 5000);
    } catch (error) {
      console.error('Export CSV error:', error);
      setExportError('Failed to export as CSV. Please try again.');

      // Clear error message after 5 seconds
      setTimeout(() => {
        setExportError(null);
      }, 5000);
    } finally {
      setIsExporting(false);
    }
  };

  /**
   * Open the confirmation popover anchored to the "Remove Starter Entries"
   * button. The actual delete runs from confirmRemoveStarters when the user
   * confirms.
   */
  const handleRequestRemoveStarters = (e) => {
    setRemoveStartersAnchor(e.currentTarget);
  };

  /**
   * Confirmed by user via the popover — call the bulk-delete endpoint and
   * show a count-aware success message or a friendly empty-state message.
   */
  const confirmRemoveStarters = async () => {
    setRemoveStartersAnchor(null);
    setIsRemovingStarters(true);
    setRemoveStartersError(null);
    setRemoveStartersSuccess(null);

    try {
      const { deletedCount } = await knowledgeService.removeStarterEntries();
      setRemoveStartersSuccess(
        deletedCount === 0
          ? 'No starter entries to remove — your knowledge base is fully your own.'
          : `Removed ${deletedCount} starter ${deletedCount === 1 ? 'entry' : 'entries'}.`
      );

      // Clear success message after 5 seconds
      setTimeout(() => {
        setRemoveStartersSuccess(null);
      }, 5000);
    } catch (error) {
      console.error('Remove starters error:', error);
      setRemoveStartersError('Could not remove starter entries. Please try again.');

      // Clear error message after 5 seconds
      setTimeout(() => {
        setRemoveStartersError(null);
      }, 5000);
    } finally {
      setIsRemovingStarters(false);
    }
  };

  // =========================================
  // Plan & Usage Banner
  // =========================================

  const getTierAccentClass = () => {
    const role = user?.role;
    const tier = user?.tier;
    if (role === 'admin') return 'plan-banner--admin';
    if (role === 'staff') return 'plan-banner--staff';
    if (tier === 'champion') return 'plan-banner--champion';
    if (tier === 'enterprise') return 'plan-banner--enterprise';
    if (tier === 'subscriber') return 'plan-banner--subscriber';
    if (tier === 'alpha') return 'plan-banner--alpha';
    return 'plan-banner--freemium';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  const renderPlanBanner = () => {
    if (!user) return null;
    const role = user.role;
    const tier = user.tier;

    // Admin and Staff override tier display
    if (role === 'admin') {
      return (
        <div className={`plan-banner ${getTierAccentClass()}`}>
          <div className="plan-banner-name">Administrator</div>
          <p className="plan-banner-description">
            Unlimited access to all features and system management.
          </p>
        </div>
      );
    }

    if (role === 'staff') {
      return (
        <div className={`plan-banner ${getTierAccentClass()}`}>
          <div className="plan-banner-name">Staff</div>
          <p className="plan-banner-description">
            Unlimited access to all features.
          </p>
        </div>
      );
    }

    if (tier === 'alpha') {
      return (
        <div className={`plan-banner ${getTierAccentClass()}`}>
          <div className="plan-banner-name">Alpha Member</div>
          <p className="plan-banner-description">
            Thank you for being one of CKG's founding users. You have full subscriber-level
            access with no expiration — including Phil and 28 AI Explorer queries per week.
          </p>
          {user.tierStartDate && (
            <p className="plan-banner-meta">
              Member since {formatDate(user.tierStartDate)}
            </p>
          )}
        </div>
      );
    }

    if (tier === 'champion') {
      return (
        <div className={`plan-banner ${getTierAccentClass()}`}>
          <div className="plan-banner-name">Champion</div>
          <p className="plan-banner-description">
            Thank you for your commitment to CKG's mission. You have full subscriber-level
            access{user.tierExpiresAt ? ` through ${formatDate(user.tierExpiresAt)}` : ''}.
          </p>
          {user.tierStartDate && (
            <p className="plan-banner-meta">
              Champion since {formatDate(user.tierStartDate)}
            </p>
          )}
        </div>
      );
    }

    if (tier === 'subscriber') {
      return (
        <div className={`plan-banner ${getTierAccentClass()}`}>
          <div className="plan-banner-name">Subscriber</div>
          <p className="plan-banner-description">
            You have full access to all CKG features including Phil and 28 AI Explorer
            queries per week.
          </p>
          <p className="plan-banner-meta">Subscription management coming soon</p>
        </div>
      );
    }

    if (tier === 'enterprise') {
      return (
        <div className={`plan-banner ${getTierAccentClass()}`}>
          <div className="plan-banner-name">Enterprise</div>
          <p className="plan-banner-description">
            Your organization has full access to all CKG features.
          </p>
        </div>
      );
    }

    // Freemium (default)
    const usageText = queryUsage
      ? `${queryUsage.used} of ${queryUsage.limit} queries used this week`
      : null;
    const resetsText = queryUsage?.resetsAt
      ? ` (resets ${new Date(queryUsage.resetsAt).toLocaleDateString('en-US', { weekday: 'long' })})`
      : '';

    return (
      <div className={`plan-banner ${getTierAccentClass()}`}>
        <div className="plan-banner-name">Free</div>
        <p className="plan-banner-description">
          You have access to the browser extension, My Knowledge Entries, Unexplored Links,
          and My Journal. AI Explorer is limited to 21 queries per week.
        </p>
        {usageText && (
          <p className="plan-banner-usage">
            AI Explorer: {usageText}{resetsText}
          </p>
        )}
        <p className="plan-banner-meta">
          Subscriptions coming soon — you'll be the first to know!
        </p>
      </div>
    );
  };

  // =========================================
  // Render: Loading State
  // =========================================

  if (!user) {
    return <div className="my-account-loading">Please log in to view your account details.</div>;
  }

  // =========================================
  // Render: Main Layout
  // =========================================

  const showPasswordBanner = user?.mustChangePassword && !bannerDismissed;

  return (
    <div className="my-account">
      {renderPlanBanner()}

      {showPasswordBanner && (
        <div className="password-banner">
          <div className="password-banner-content">
            <strong>Welcome!</strong> We recommend setting a memorable password to replace your temporary one.
          </div>
          <button
            className="password-banner-dismiss"
            onClick={() => setBannerDismissed(true)}
            aria-label="Dismiss banner"
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div className="my-account-grid">

        {/* ===== COLUMN 1: Profile & Password ===== */}
        <div className="my-account-column">
          
          {/* Update Profile Card */}
          <div className="account-card">
            <h3 className="card-title">Update Profile</h3>
            
            {profileError && <div className="error-message">{profileError}</div>}
            {profileMessage && <div className="success-message">{profileMessage}</div>}
            
            <form onSubmit={handleProfileUpdate}>
              <div className="form-field">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  value={profileData.firstName}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    firstName: e.target.value
                  })}
                  required
                  disabled={isProfileLoading}
                />
              </div>

              <div className="form-field">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  value={profileData.lastName}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    lastName: e.target.value
                  })}
                  required
                  disabled={isProfileLoading}
                />
              </div>

              <div className="form-field">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={profileData.email}
                  onChange={(e) => setProfileData({
                    ...profileData,
                    email: e.target.value
                  })}
                  required
                  disabled={isProfileLoading}
                />
              </div>

              <button type="submit" className="primary-button" disabled={isProfileLoading}>
                {isProfileLoading ? 'Updating...' : 'Update Profile'}
              </button>
            </form>
          </div>

          {/* Change Password Card */}
          <div className="account-card">
            <h3 className="card-title">Change Password</h3>
            
            {passwordError && <div className="error-message">{passwordError}</div>}
            {passwordMessage && <div className="success-message">{passwordMessage}</div>}
            
            <form onSubmit={handlePasswordChange}>
              {!user?.mustChangePassword && (
              <div className="form-field">
                <label htmlFor="currentPassword">Current Password</label>
                <div className="password-input__field">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    id="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData,
                      currentPassword: e.target.value
                    })}
                    required
                    disabled={isPasswordLoading}
                  />
                  <button
                    type="button"
                    className="password-input__toggle"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    disabled={isPasswordLoading}
                    aria-label={showCurrentPassword ? "Hide password" : "Show password"}
                  >
                    {showCurrentPassword ? (
                      <EyeOff size={20} aria-hidden="true" />
                    ) : (
                      <Eye size={20} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
              )}

              <div className="form-field">
                <label htmlFor="newPassword">New Password</label>
                <div className="password-input__field">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPassword"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData,
                      newPassword: e.target.value
                    })}
                    required
                    disabled={isPasswordLoading}
                  />
                  <button
                    type="button"
                    className="password-input__toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    disabled={isPasswordLoading}
                    aria-label={showNewPassword ? "Hide password" : "Show password"}
                  >
                    {showNewPassword ? (
                      <EyeOff size={20} aria-hidden="true" />
                    ) : (
                      <Eye size={20} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <div className="form-field">
                <label htmlFor="confirmNewPassword">Confirm New Password</label>
                <div className="password-input__field">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmNewPassword"
                    value={passwordData.confirmNewPassword}
                    onChange={(e) => setPasswordData({
                      ...passwordData,
                      confirmNewPassword: e.target.value
                    })}
                    required
                    disabled={isPasswordLoading}
                  />
                  <button
                    type="button"
                    className="password-input__toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={isPasswordLoading}
                    aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} aria-hidden="true" />
                    ) : (
                      <Eye size={20} aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" className="primary-button" disabled={isPasswordLoading}>
                {isPasswordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </form>
          </div>
        </div>

        {/* ===== COLUMN 2: Welcome, Export, Delete ===== */}
        <div className="my-account-column">
          
          {/* Welcome to Constellation KG Card */}
          <div className="account-card welcome-card">
            <h3 className="card-title">Welcome to Constellation KG!</h3>
            <p className="card-description">
              Redefining the way you experience and interact with what you know and
              think starts here. Once you've installed and set up Constellation KG's
              browser extension, you can explore CKG's features using the reference
              material below.
            </p>
            <div className="welcome-buttons">
              {/* Required setup action — stays full-width and visually distinct
                  from the four optional reference topics below it. */}
              <a
                href="https://chrome.google.com/webstore/detail/hbojfmmfedjicppfomcfimhnghhaplmf"
                className="welcome-button"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Download size={18} />
                Add to Chrome
              </a>
              <div className="welcome-section-grid">
                {OVERVIEW_SECTIONS.map(({ section, label }) => (
                  <button
                    key={section}
                    type="button"
                    className="welcome-section-button"
                    onClick={() => setActiveSection(section)}
                  >
                    <FileText size={16} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Export Knowledge Base Card */}
          <div className="account-card">
            <h3 className="card-title">Export My Knowledge Base</h3>
            <p className="card-description">
              Constellation KG doesn't hold you hostage. Want a copy of your work? 
              No problem: export it as a JSON file or a CSV file by clicking one of 
              the buttons below:
            </p>
            
            {/* Success message */}
            {exportSuccess && (
              <div className="export-message export-message--success">
                {exportSuccess}
              </div>
            )}
            
            {/* Error message */}
            {exportError && (
              <div className="export-message export-message--error">
                {exportError}
              </div>
            )}
            
            <div className="action-buttons">
              <button 
                className="secondary-button" 
                onClick={handleExportJSON}
                disabled={isExporting}
              >
                <DatabaseBackup size={16} />
                {isExporting ? 'Exporting...' : 'JSON'}
              </button>
              <button 
                className="secondary-button"
                onClick={handleExportCSV}
                disabled={isExporting}
              >
                <DatabaseBackup size={16} />
                {isExporting ? 'Exporting...' : 'CSV'}
              </button>
            </div>
          </div>

          {/* Remove Starter Entries Card */}
          <div className="account-card">
            <h3 className="card-title">Remove Starter Entries</h3>
            <p className="card-description">
              Your knowledge base started with a small set of curated entries
              to help you orient. Once you've built up your own captures, you
              can clear those starters out — your own entries are never affected.
            </p>

            {removeStartersSuccess && (
              <div className="export-message export-message--success">
                {removeStartersSuccess}
              </div>
            )}
            {removeStartersError && (
              <div className="export-message export-message--error">
                {removeStartersError}
              </div>
            )}

            <div className="action-buttons">
              <button
                className="danger-button"
                onClick={handleRequestRemoveStarters}
                disabled={isRemovingStarters}
              >
                <Trash2 size={16} />
                {isRemovingStarters ? 'Removing...' : 'Remove Starter Entries'}
              </button>
            </div>
          </div>

          <DeleteConfirmPopover
            pendingDelete={removeStartersAnchor ? { anchor: removeStartersAnchor } : null}
            onDismiss={() => setRemoveStartersAnchor(null)}
            onConfirm={confirmRemoveStarters}
            message={
              'This will remove all starter entries from your knowledge base. ' +
              "Any starter entries where you've added or edited a thought will " +
              'not be affected — those are yours now.'
            }
            confirmLabel="Remove Starters"
            dismissOnScroll={false}
          />

          {/* Delete Account Card */}
          <div className="account-card coming-soon-card">
            <div className="coming-soon-badge">Coming Soon</div>
            <h3 className="card-title">Delete My Account &amp; Data</h3>
            <p className="card-description">
              Account deletion is coming. Until then, email{' '}
              <a href="mailto:info@constellationkg.com" className="inline-link">info@constellationkg.com</a>{' '}
              and we will delete your account and data by hand within 30 days, as
              our <a href="/terms" className="inline-link">Terms of Service</a> describe.
            </p>
            <div className="action-buttons">
              <button className="danger-button" disabled>
                <Trash2 size={16} />
                Delete My Data
              </button>
              <button className="danger-button" disabled>
                <Trash2 size={16} />
                Delete My Account
              </button>
            </div>
          </div>

        </div>
      </div>

      <WelcomeModal
        isOpen={activeSection !== null}
        section={activeSection}
        onClose={() => setActiveSection(null)}
      />

    </div>
  );
};

export default MyAccount;
